import { faArrowLeft, faArrowRight, faChevronDown, faCopy, faEllipsisV, faHeart, faLink, faPen, faPlus, faRefresh, faSearch, faSpinner, faStop, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";

const StyleGuide = () => {
  const [activeTab, setActiveTab] = useState("brand");

  const tabs = [
    { id: "brand", label: "Brand Alignment" },
    { id: "buttons", label: "Buttons" },
    { id: "typography", label: "Typography" },
    { id: "colors", label: "Colors" },
    { id: "forms", label: "Forms" },
    { id: "cards", label: "Cards" },
    { id: "badges", label: "Badges" },
    { id: "loading", label: "Loading" },
    { id: "spacing", label: "Spacing" },
  ];

  return (
    <div className="min-h-screen bg-base-200">
      {/* Header */}
      <div className="bg-base-100 border-b border-base-content/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-xl font-bold text-base-content mb-4">Monika Style Guide</h1>
          <div className="flex gap-1 overflow-x-auto pb-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-content"
                    : "text-base-content/50 hover:text-base-content/70 hover:bg-base-content/5"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === "brand" && <BrandAlignmentSection />}
        {activeTab === "buttons" && <ButtonsSection />}
        {activeTab === "typography" && <TypographySection />}
        {activeTab === "colors" && <ColorsSection />}
        {activeTab === "forms" && <FormsSection />}
        {activeTab === "cards" && <CardsSection />}
        {activeTab === "badges" && <BadgesSection />}
        {activeTab === "loading" && <LoadingSection />}
        {activeTab === "spacing" && <SpacingSection />}
      </div>
    </div>
  );
};

// ============================================
// BRAND ALIGNMENT SECTION
// ============================================
const BrandAlignmentSection = () => (
  <div className="space-y-12">
    {/* Overview */}
    <Section title="Marketing Site vs App Alignment">
      <p className="text-sm text-base-content/60 mb-6">
        Comparison between <a href="https://monikanaming.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">monikanaming.com</a> and the Monika app to ensure brand consistency.
      </p>

      {/* Side by side comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Marketing Site */}
        <div className="rounded-xl overflow-hidden border border-base-content/10">
          <div className="bg-[#d4c4b0] p-8 text-center">
            <div className="text-4xl font-display tracking-tight text-[#221f20] mb-3" style={{ fontFamily: 'serif' }}>
              MONIKA
            </div>
            <p className="text-lg text-[#221f20]/80" style={{ fontFamily: 'serif' }}>A moniker delivery system.</p>
            <p className="text-sm text-[#221f20]/60 mt-2">Marketing site hero</p>
          </div>
          <div className="bg-white p-4 flex justify-center">
            <svg viewBox="0 0 200 40" className="w-48 h-10">
              {/* Geometric pattern representation */}
              {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
                <g key={i} transform={`translate(${i * 25}, 0)`}>
                  <path d="M0 40 Q12.5 0 25 40" fill="none" stroke="#221f20" strokeWidth="8" />
                </g>
              ))}
            </svg>
          </div>
          <div className="p-4 bg-base-200/50">
            <p className="text-xs text-base-content/50 text-center">Black & white geometric patterns as visual language</p>
          </div>
        </div>

        {/* App */}
        <div className="rounded-xl overflow-hidden border border-base-content/10">
          <div className="bg-base-100 p-8 text-center">
            <div className="text-4xl font-display tracking-tight text-base-content mb-3">
              MONIKA
            </div>
            <p className="text-lg text-base-content/70">Brand naming tool</p>
            <p className="text-sm text-base-content/40 mt-2">App header</p>
          </div>
          <div className="bg-base-100 p-4 flex justify-center gap-3">
            <button className="px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider">
              Primary Action
            </button>
            <button className="px-4 py-2 text-sm text-base-content/50">Cancel</button>
          </div>
          <div className="p-4 bg-base-200/50">
            <p className="text-xs text-base-content/50 text-center">Orange accent for actions, warm cream backgrounds</p>
          </div>
        </div>
      </div>
    </Section>

    {/* Color Alignment */}
    <Section title="Color Alignment">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-base-content/10">
              <th className="text-left py-3 text-base-content/50 font-medium">Element</th>
              <th className="text-left py-3 text-base-content/50 font-medium">Marketing Site</th>
              <th className="text-left py-3 text-base-content/50 font-medium">App</th>
              <th className="text-left py-3 text-base-content/50 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-content/5">
            <tr>
              <td className="py-3 text-base-content/70">Background</td>
              <td className="py-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-[#d4c4b0] border border-base-content/10"></div>
                  <span className="text-xs text-base-content/50">Warm beige</span>
                </div>
              </td>
              <td className="py-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-[#f1efea] border border-base-content/10"></div>
                  <span className="text-xs text-base-content/50">#f1efea</span>
                </div>
              </td>
              <td className="py-3"><span className="text-success text-xs">✓ Similar warmth</span></td>
            </tr>
            <tr>
              <td className="py-3 text-base-content/70">Dark/Text</td>
              <td className="py-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-[#221f20]"></div>
                  <span className="text-xs text-base-content/50">#221f20</span>
                </div>
              </td>
              <td className="py-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-[#221f20]"></div>
                  <span className="text-xs text-base-content/50">#221f20</span>
                </div>
              </td>
              <td className="py-3"><span className="text-success text-xs">✓ Identical</span></td>
            </tr>
            <tr>
              <td className="py-3 text-base-content/70">Primary Accent</td>
              <td className="py-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-[#221f20]"></div>
                  <span className="text-xs text-base-content/50">Black (buttons)</span>
                </div>
              </td>
              <td className="py-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-[#c76f31]"></div>
                  <span className="text-xs text-base-content/50">#c76f31 orange</span>
                </div>
              </td>
              <td className="py-3"><span className="text-info text-xs">◐ Intentionally different</span></td>
            </tr>
            <tr>
              <td className="py-3 text-base-content/70">Visual Pattern</td>
              <td className="py-3">
                <div className="flex items-center gap-2">
                  <svg viewBox="0 0 32 16" className="w-8 h-4">
                    <path d="M0 16 Q8 0 16 16" fill="none" stroke="#221f20" strokeWidth="3" />
                    <path d="M16 16 Q24 0 32 16" fill="none" stroke="#221f20" strokeWidth="3" />
                  </svg>
                  <span className="text-xs text-base-content/50">Geometric arcs</span>
                </div>
              </td>
              <td className="py-3">
                <span className="text-xs text-base-content/40">Not used</span>
              </td>
              <td className="py-3"><span className="text-warning text-xs">○ Could add</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>

    {/* Typography Alignment */}
    <Section title="Typography Alignment">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-base-200/50 rounded-lg p-6">
          <h4 className="text-xs font-medium uppercase tracking-wider text-base-content/40 mb-4">Marketing Site</h4>
          <div className="space-y-4">
            <div>
              <span className="text-[10px] text-base-content/30">Logo/Wordmark</span>
              <p className="text-2xl" style={{ fontFamily: 'serif', letterSpacing: '0.05em' }}>MONIKA</p>
              <p className="text-xs text-base-content/40 mt-1">Custom serif display font</p>
            </div>
            <div>
              <span className="text-[10px] text-base-content/30">Headlines</span>
              <p className="text-xl" style={{ fontFamily: 'serif' }}>A moniker delivery system.</p>
              <p className="text-xs text-base-content/40 mt-1">Elegant serif</p>
            </div>
            <div>
              <span className="text-[10px] text-base-content/30">Body</span>
              <p className="text-base">Clean and professional.</p>
              <p className="text-xs text-base-content/40 mt-1">Sans-serif</p>
            </div>
          </div>
        </div>

        <div className="bg-base-200/50 rounded-lg p-6">
          <h4 className="text-xs font-medium uppercase tracking-wider text-base-content/40 mb-4">App</h4>
          <div className="space-y-4">
            <div>
              <span className="text-[10px] text-base-content/30">Logo/Wordmark</span>
              <p className="text-2xl font-display">MONIKA</p>
              <p className="text-xs text-base-content/40 mt-1">Arizona Flare</p>
            </div>
            <div>
              <span className="text-[10px] text-base-content/30">Brand Names</span>
              <p className="text-xl font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555]">Brandify</p>
              <p className="text-xs text-base-content/40 mt-1">Oswald, skewed</p>
            </div>
            <div>
              <span className="text-[10px] text-base-content/30">Body</span>
              <p className="text-base">Clean and professional.</p>
              <p className="text-xs text-base-content/40 mt-1">Apercu / system sans</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-info/10 rounded-lg border border-info/20">
        <p className="text-sm text-info">
          <strong>Note:</strong> The marketing site uses a custom serif for the MONIKA wordmark. Consider using the same font in the app header for stronger brand continuity.
        </p>
      </div>
    </Section>

    {/* Visual Language */}
    <Section title="Visual Language & Patterns">
      <div className="mb-6">
        <h4 className="text-sm font-medium text-base-content/70 mb-3">Marketing Site Geometric Pattern</h4>
        <p className="text-xs text-base-content/50 mb-4">The curved arc pattern is a distinctive brand element that could be incorporated into the app.</p>

        <div className="bg-white rounded-xl p-8 border border-base-content/10">
          <svg viewBox="0 0 400 60" className="w-full h-16">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(i => (
              <g key={i} transform={`translate(${i * 25}, 0)`}>
                <path d="M0 60 Q12.5 0 25 60" fill="none" stroke="#221f20" strokeWidth="6" />
              </g>
            ))}
          </svg>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-base-200/50 rounded-lg p-4">
          <h5 className="text-xs font-medium text-base-content/50 mb-2">Potential Use: Loading</h5>
          <div className="bg-base-100 rounded-lg p-4 flex items-center justify-center h-24">
            <svg viewBox="0 0 100 30" className="w-24 h-8 animate-pulse">
              {[0, 1, 2, 3].map(i => (
                <g key={i} transform={`translate(${i * 25}, 0)`}>
                  <path d="M0 30 Q12.5 0 25 30" fill="none" stroke="#c76f31" strokeWidth="4" opacity={0.3 + i * 0.2} />
                </g>
              ))}
            </svg>
          </div>
        </div>

        <div className="bg-base-200/50 rounded-lg p-4">
          <h5 className="text-xs font-medium text-base-content/50 mb-2">Potential Use: Empty State</h5>
          <div className="bg-base-100 rounded-lg p-4 flex flex-col items-center justify-center h-24">
            <svg viewBox="0 0 60 20" className="w-16 h-5 mb-2 opacity-20">
              {[0, 1, 2].map(i => (
                <g key={i} transform={`translate(${i * 20}, 0)`}>
                  <path d="M0 20 Q10 0 20 20" fill="none" stroke="#221f20" strokeWidth="3" />
                </g>
              ))}
            </svg>
            <span className="text-xs text-base-content/40">No names yet</span>
          </div>
        </div>

        <div className="bg-base-200/50 rounded-lg p-4">
          <h5 className="text-xs font-medium text-base-content/50 mb-2">Potential Use: Divider</h5>
          <div className="bg-base-100 rounded-lg p-4 flex flex-col items-center justify-center h-24">
            <div className="w-full border-t border-base-content/10 relative">
              <svg viewBox="0 0 80 16" className="w-20 h-4 absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-base-100 px-2">
                {[0, 1, 2, 3].map(i => (
                  <g key={i} transform={`translate(${i * 20}, 0)`}>
                    <path d="M0 16 Q10 0 20 16" fill="none" stroke="#221f20" strokeWidth="2" />
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Section>

    {/* Recommendations */}
    <Section title="Alignment Recommendations">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-success/10 rounded-lg p-4 border border-success/20">
          <h4 className="text-sm font-medium text-success mb-2">✓ Already Aligned</h4>
          <ul className="text-sm text-base-content/60 space-y-1">
            <li>• Warm background tones (beige/cream family)</li>
            <li>• Dark text color (#221f20)</li>
            <li>• Clean, minimal aesthetic</li>
            <li>• Pill-shaped buttons</li>
            <li>• Professional, agency-like feel</li>
          </ul>
        </div>

        <div className="bg-warning/10 rounded-lg p-4 border border-warning/20">
          <h4 className="text-sm font-medium text-warning mb-2">○ Opportunities</h4>
          <ul className="text-sm text-base-content/60 space-y-1">
            <li>• Add geometric arc pattern to app</li>
            <li>• Use marketing wordmark font in app header</li>
            <li>• Consider serif for page titles</li>
            <li>• Could add black button variant for secondary actions</li>
          </ul>
        </div>

        <div className="bg-info/10 rounded-lg p-4 border border-info/20 md:col-span-2">
          <h4 className="text-sm font-medium text-info mb-2">◐ Intentional Differences (Keep)</h4>
          <ul className="text-sm text-base-content/60 space-y-1">
            <li>• <strong>Orange accent (#c76f31)</strong> - Adds energy and warmth to the functional app that the minimal marketing site doesn't need</li>
            <li>• <strong>Oswald for brand names</strong> - Distinctive display of generated names, different from marketing headlines</li>
            <li>• <strong>More UI chrome</strong> - App needs buttons, badges, forms that marketing site doesn't</li>
          </ul>
        </div>
      </div>
    </Section>
  </div>
);

// ============================================
// BUTTONS SECTION
// ============================================
const ButtonsSection = () => (
  <div className="space-y-12">
    {/* Current State - Fixed */}
    <Section title="Current State: Consistent ✓" variant="success">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ExampleCard title="Projects Page" status="ok">
          <button className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-content rounded-full text-xs font-medium uppercase tracking-wider">
            <FontAwesomeIcon icon={faPlus} />
            New Project
          </button>
          <Code>rounded-full px-5 py-2.5 ✓</Code>
        </ExampleCard>

        <ExampleCard title="Process Page" status="ok">
          <button className="px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider">
            Add
          </button>
          <Code>rounded-full px-5 py-2.5 ✓</Code>
        </ExampleCard>

        <ExampleCard title="Stop Generation" status="ok">
          <button className="px-5 py-2.5 flex items-center gap-2 bg-base-content text-base-100 rounded-full text-sm font-medium uppercase tracking-wider">
            <FontAwesomeIcon icon={faStop} />
            Stop
          </button>
          <Code>bg-base-content (dark, elegant) ✓</Code>
        </ExampleCard>

        <ExampleCard title="Edit Modal Cancel" status="ok">
          <button className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70 transition-colors">
            Cancel
          </button>
          <Code>/50 opacity ✓</Code>
        </ExampleCard>

        <ExampleCard title="Delete Modal Cancel" status="ok">
          <button className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70 transition-colors">
            Cancel
          </button>
          <Code>/50 opacity ✓</Code>
        </ExampleCard>

        <ExampleCard title="Pagination" status="ok">
          <div className="flex gap-2">
            <button className="p-2 rounded text-base-content/50 hover:text-base-content">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <button className="p-2 rounded text-base-content/50 hover:text-base-content">
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          <Code>Simple and functional ✓</Code>
        </ExampleCard>
      </div>
    </Section>

    {/* Recommended System */}
    <Section title="Recommended: Unified Button System" variant="success">
      {/* Circular Icon Button */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-2">Circular Icon Button (Navigation / Primary Actions)</h3>
        <p className="text-xs text-base-content/50 mb-4">Best for navigation, prominent actions, and sign-in buttons. Uses ring hover effect.</p>
        <div className="flex flex-wrap gap-8 items-start mb-3">
          <div className="text-center">
            <button className="group flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center transition-all group-hover:ring-4 group-hover:ring-primary/20">
                <FontAwesomeIcon icon={faPlus} className="text-primary-content text-lg" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-base-content/50 group-hover:text-base-content transition-colors">
                Projects
              </span>
            </button>
            <p className="text-[10px] text-base-content/30 mt-2">w-14 h-14</p>
          </div>
          <div className="text-center">
            <button className="group flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center transition-all group-hover:ring-4 group-hover:ring-primary/20">
                <FontAwesomeIcon icon={faHeart} className="text-primary-content" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-base-content/50 group-hover:text-base-content transition-colors">
                Liked
              </span>
            </button>
            <p className="text-[10px] text-base-content/30 mt-2">w-12 h-12 (mobile)</p>
          </div>
        </div>
        <Code>group flex flex-col items-center gap-2 → w-14 h-14 rounded-full bg-primary group-hover:ring-4 group-hover:ring-primary/20</Code>
      </div>

      {/* Primary */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-2">Primary Action (Form Submissions / CTAs)</h3>
        <p className="text-xs text-base-content/50 mb-4">For form buttons, modal actions, inline CTAs.</p>
        <div className="flex flex-wrap gap-4 items-center mb-3">
          <button className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider">
            <FontAwesomeIcon icon={faPlus} />
            New Project
          </button>
          <button className="px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider">
            Save Changes
          </button>
          <button className="px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider opacity-50 cursor-not-allowed">
            Disabled
          </button>
        </div>
        <Code>px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider</Code>
      </div>

      {/* Secondary */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-2">Secondary / Cancel</h3>
        <p className="text-xs text-base-content/50 mb-4">Cancel, Back, Dismiss actions</p>
        <div className="flex flex-wrap gap-4 items-center mb-3">
          <button className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70 transition-colors">Cancel</button>
          <button className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70 transition-colors">Back</button>
        </div>
        <Code>px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70 transition-colors</Code>
      </div>

      {/* Destructive */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-2">Destructive</h3>
        <p className="text-xs text-base-content/50 mb-4">Delete confirmations - dark and serious, not alarming red</p>
        <div className="flex flex-wrap gap-4 items-center mb-3">
          <button className="px-5 py-2 bg-base-content text-base-100 rounded-full text-sm font-medium hover:bg-base-content/80 transition-colors">
            Delete
          </button>
          <button className="px-5 py-2 bg-base-content text-base-100 rounded-full text-sm font-medium flex items-center gap-2">
            <FontAwesomeIcon icon={faStop} />
            Stop
          </button>
        </div>
        <Code>px-5 py-2 bg-base-content text-base-100 rounded-full text-sm font-medium</Code>
      </div>

      {/* Ghost */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-2">Ghost / Tertiary</h3>
        <p className="text-xs text-base-content/50 mb-4">Secondary actions: Upload, Refresh, Reopen</p>
        <div className="flex flex-wrap gap-6 items-center mb-3">
          <button className="text-sm text-base-content/40 hover:text-base-content/70 transition-colors">Upload</button>
          <button className="text-sm text-base-content/40 hover:text-base-content/70 transition-colors">Regenerate</button>
          <button className="text-sm text-base-content/40 hover:text-error transition-colors">Clear All</button>
        </div>
        <Code>text-sm text-base-content/40 hover:text-base-content/70 transition-colors</Code>
      </div>

      {/* Icon Buttons */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-2">Icon Buttons</h3>
        <p className="text-xs text-base-content/50 mb-4">Consistent circular buttons for all icon-only actions</p>
        <div className="flex flex-wrap gap-3 items-center mb-3">
          {[faEllipsisV, faArrowLeft, faArrowRight, faXmark, faPen, faTrash].map((icon, i) => (
            <button key={i} className="w-9 h-9 flex items-center justify-center rounded-full text-base-content/30 hover:text-base-content/70 hover:bg-base-content/10 transition-colors">
              <FontAwesomeIcon icon={icon} />
            </button>
          ))}
          <button className="w-9 h-9 flex items-center justify-center rounded-full text-base-content/30 hover:text-primary hover:bg-primary/10 transition-colors">
            <FontAwesomeIcon icon={faHeart} />
          </button>
        </div>
        <Code>w-9 h-9 flex items-center justify-center rounded-full text-base-content/30 hover:text-base-content/70 hover:bg-base-content/10</Code>
      </div>

      {/* Dropdown Menu */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-2">Dropdown Menu Items</h3>
        <div className="inline-block bg-base-100 rounded-xl shadow-md border border-base-content/5 py-2 min-w-[160px]">
          <button className="w-full px-4 py-2.5 text-left text-sm text-base-content/70 hover:bg-base-content/5 flex items-center gap-3 transition-colors">
            <FontAwesomeIcon icon={faPen} className="text-sm text-base-content/40 w-4" />
            Edit
          </button>
          <button className="w-full px-4 py-2.5 text-left text-sm text-base-content/70 hover:bg-base-content/5 flex items-center gap-3 transition-colors">
            <FontAwesomeIcon icon={faCopy} className="text-sm text-base-content/40 w-4" />
            Copy
          </button>
          <button className="w-full px-4 py-2.5 text-left text-sm text-base-content/70 hover:bg-error/5 hover:text-error flex items-center gap-3 transition-colors">
            <FontAwesomeIcon icon={faTrash} className="text-sm text-base-content/40 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Toggle */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-2">Toggle / Filter</h3>
        <div className="flex flex-wrap gap-3 items-center mb-3">
          <button className="px-3 py-1.5 rounded-lg border text-xs transition-colors border-base-content/10 text-base-content/40 hover:text-base-content/60">
            Inactive
          </button>
          <button className="px-3 py-1.5 rounded-lg border text-xs transition-colors border-primary/30 text-primary bg-primary/5">
            Active
          </button>
        </div>
        <Code>Active: border-primary/30 text-primary bg-primary/5</Code>
      </div>

      {/* Hover States Warning */}
      <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
        <h3 className="text-sm font-semibold text-warning mb-2">⚠️ Hover States Rule</h3>
        <p className="text-sm text-base-content/60 mb-3">
          <strong>NO scale transforms on hover.</strong> Do not use <code className="text-xs bg-base-content/10 px-1 rounded">hover:scale-*</code> or any transform effects on cards/tiles.
        </p>
        <p className="text-sm text-base-content/60 mb-2">Keep hover effects subtle:</p>
        <ul className="text-sm text-base-content/60 space-y-1 ml-4">
          <li>• Cards/Tiles: <code className="text-xs bg-base-content/10 px-1 rounded">hover:border-primary/30</code> (border color only)</li>
          <li>• Circular buttons: <code className="text-xs bg-base-content/10 px-1 rounded">group-hover:ring-4 group-hover:ring-primary/20</code></li>
          <li>• Text/Links: <code className="text-xs bg-base-content/10 px-1 rounded">hover:text-primary</code></li>
          <li>• Icon buttons: <code className="text-xs bg-base-content/10 px-1 rounded">hover:bg-base-content/10</code></li>
        </ul>
      </div>
    </Section>
  </div>
);

// ============================================
// TYPOGRAPHY SECTION
// ============================================
const TypographySection = () => (
  <div className="space-y-12">
    <Section title="Typography System">
      {/* Headings */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Headings</h3>
        <div className="space-y-4 bg-base-100 rounded-xl p-6 border border-base-content/10">
          <div>
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Page Title</span>
            <h1 className="text-3xl font-bold text-base-content">Projects Overview</h1>
            <Code>text-3xl font-bold text-base-content</Code>
          </div>
          <div className="border-t border-base-content/5 pt-4">
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Section Title</span>
            <h2 className="text-xl font-semibold text-base-content">Generated Names</h2>
            <Code>text-xl font-semibold text-base-content</Code>
          </div>
          <div className="border-t border-base-content/5 pt-4">
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Section Header (with border)</span>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 pb-2 border-b border-base-content/10">
              Trademark Results <span className="text-base-content/40 font-normal">(24)</span>
            </h3>
            <Code>text-sm font-semibold uppercase tracking-wider text-base-content/70</Code>
          </div>
        </div>
      </div>

      {/* Brand Name Display */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Brand Name Display (Oswald)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["Small", "Normal", "Large", "Hero"].map((size, i) => (
            <div key={size} className="bg-base-100 rounded-xl p-4 border border-base-content/10 flex flex-col items-center justify-center min-h-[100px]">
              <span className="text-[10px] text-base-content/30 uppercase tracking-wider mb-2">{size}</span>
              <span className={`font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555] ${
                i === 0 ? "text-base" : i === 1 ? "text-xl" : i === 2 ? "text-2xl" : "text-4xl"
              }`}>
                Brandify
              </span>
            </div>
          ))}
        </div>
        <Code>font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555]</Code>
      </div>

      {/* Body Text */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Body Text</h3>
        <div className="space-y-3 bg-base-100 rounded-xl p-6 border border-base-content/10">
          <div>
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Body Text</span>
            <p className="text-base-content">This is standard body text used throughout the application.</p>
            <Code>text-base-content</Code>
          </div>
          <div>
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Secondary Text</span>
            <p className="text-base-content/70">This is secondary text with reduced emphasis.</p>
            <Code>text-base-content/70</Code>
          </div>
          <div>
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Muted Text</span>
            <p className="text-base-content/50">This is muted text for less important information.</p>
            <Code>text-base-content/50</Code>
          </div>
          <div>
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Small Text</span>
            <p className="text-sm text-base-content/60">This is small text for metadata and timestamps.</p>
            <Code>text-sm text-base-content/60</Code>
          </div>
          <div>
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Extra Small / Labels</span>
            <p className="text-xs text-base-content/40">This is extra small text for labels and hints.</p>
            <Code>text-xs text-base-content/40</Code>
          </div>
        </div>
      </div>

      {/* Links */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Links</h3>
        <div className="flex flex-wrap gap-6 items-center bg-base-100 rounded-xl p-6 border border-base-content/10">
          <button type="button" className="text-primary hover:underline">Primary Link</button>
          <button type="button" className="text-base-content/50 hover:text-primary transition-colors">Muted Link</button>
          <button type="button" className="text-sm text-primary/70 hover:text-primary flex items-center gap-1">
            <FontAwesomeIcon icon={faLink} className="text-xs" />
            Link with Icon
          </button>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Breadcrumbs</h3>
        <div className="bg-base-100 rounded-xl p-6 border border-base-content/10">
          <nav className="flex items-center gap-2 text-xs uppercase tracking-wider">
            <span className="text-base-content/40 hover:text-base-content transition-colors cursor-pointer">Home</span>
            <span className="text-base-content/20">→</span>
            <span className="text-base-content/40 hover:text-base-content transition-colors cursor-pointer">Projects</span>
            <span className="text-base-content/20">→</span>
            <span className="text-primary font-medium">Project Name</span>
          </nav>
          <Code>Links: text-base-content/40 | Arrow: text-base-content/20 | Active: text-primary font-medium</Code>
        </div>
      </div>
    </Section>
  </div>
);

// ============================================
// COLORS SECTION
// ============================================
const ColorsSection = () => (
  <div className="space-y-12">
    <Section title="Color Palette">
      {/* Brand Colors */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Brand Colors</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <ColorSwatch name="Primary (Orange)" color="#c76f31" variable="primary" />
          <ColorSwatch name="Base 100 (Light)" color="#f1efea" variable="base-100" dark />
          <ColorSwatch name="Base 200 (Gray)" color="#dddad4" variable="base-200" dark />
          <ColorSwatch name="Base Content (Dark)" color="#221f20" variable="base-content" />
          <ColorSwatch name="Secondary (Grey)" color="#7e88a5" variable="secondary" />
          <ColorSwatch name="Accent (Olive)" color="#a79f8c" variable="accent" />
        </div>
      </div>

      {/* Semantic Colors */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Semantic Colors</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ColorSwatch name="Success" color="#22c55e" variable="success" />
          <ColorSwatch name="Info" color="#3b82f6" variable="info" />
          <ColorSwatch name="Warning" color="#f59e0b" variable="warning" />
          <ColorSwatch name="Error" color="#ef4444" variable="error" />
        </div>
      </div>

      {/* Opacity Scale */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Opacity Scale (base-content)</h3>
        <p className="text-xs text-base-content/50 mb-4">Used for text and borders throughout the app</p>
        <div className="flex flex-wrap gap-2">
          {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(opacity => (
            <div key={opacity} className="text-center">
              <div className={`w-16 h-12 rounded-lg bg-base-content/${opacity} mb-1`}></div>
              <span className="text-[10px] text-base-content/50">/{opacity}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Grade Colors */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Grade Colors</h3>
        <div className="flex flex-wrap gap-4">
          <GradeBadge grade="A" />
          <GradeBadge grade="B" />
          <GradeBadge grade="C" />
          <GradeBadge grade="D" />
          <GradeBadge grade="F" />
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
          <Code>A: bg-success/20 text-success</Code>
          <Code>B: bg-info/20 text-info</Code>
          <Code>C: bg-warning/20 text-warning</Code>
          <Code>D/F: bg-error/20 text-error</Code>
        </div>
      </div>
    </Section>
  </div>
);

// ============================================
// FORMS SECTION
// ============================================
const FormsSection = () => (
  <div className="space-y-12">
    <Section title="Form Elements">
      {/* Text Inputs */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Text Inputs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-base-100 rounded-xl p-6 border border-base-content/10">
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Box Style (Default)</span>
            <div className="relative mt-2">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/30 text-xs" />
              <input
                type="text"
                placeholder="Search projects..."
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-base-200 border border-base-content/10 rounded-lg focus:outline-none focus:border-primary"
              />
            </div>
            <Code>bg-base-200 border border-base-content/10 rounded-lg focus:border-primary</Code>
          </div>

          <div className="bg-base-100 rounded-xl p-6 border border-base-content/10">
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider">Underline Style</span>
            <input
              type="text"
              placeholder="Enter value..."
              className="w-full px-0 py-2 mt-2 text-sm bg-transparent border-b border-base-content/20 focus:outline-none focus:border-primary"
            />
            <Code>bg-transparent border-b border-base-content/20 focus:border-primary</Code>
          </div>
        </div>
      </div>

      {/* Labels */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Labels</h3>
        <div className="bg-base-100 rounded-xl p-6 border border-base-content/10 space-y-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-base-content/40 mb-2">
              Project Name
            </label>
            <input type="text" className="w-full px-3 py-2 text-sm bg-base-200 border border-base-content/10 rounded-lg" defaultValue="My Project" />
          </div>
          <Code>text-xs font-medium uppercase tracking-wider text-base-content/40 mb-2</Code>
        </div>
      </div>

      {/* Checkboxes & Radios */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Checkboxes & Radios</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-base-100 rounded-xl p-6 border border-base-content/10">
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider mb-3 block">Checkboxes</span>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-base-content/70 hover:text-base-content">
                <input type="checkbox" className="checkbox checkbox-xs checkbox-primary" defaultChecked />
                Option selected
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-base-content/70 hover:text-base-content">
                <input type="checkbox" className="checkbox checkbox-xs checkbox-primary" />
                Option unselected
              </label>
            </div>
            <Code>checkbox checkbox-xs checkbox-primary</Code>
          </div>

          <div className="bg-base-100 rounded-xl p-6 border border-base-content/10">
            <span className="text-[10px] text-base-content/30 uppercase tracking-wider mb-3 block">Radio Buttons</span>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-base-content/70 hover:text-base-content">
                <input type="radio" name="example" className="radio radio-xs radio-primary" defaultChecked />
                Option A
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-base-content/70 hover:text-base-content">
                <input type="radio" name="example" className="radio radio-xs radio-primary" />
                Option B
              </label>
            </div>
            <Code>radio radio-xs radio-primary</Code>
          </div>
        </div>
      </div>

      {/* Select / Dropdown */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Select / Dropdown</h3>
        <div className="bg-base-100 rounded-xl p-6 border border-base-content/10">
          <label className="block text-xs font-medium uppercase tracking-wider text-base-content/40 mb-2">
            Status
          </label>
          <div className="relative inline-block">
            <select className="appearance-none px-4 py-2 pr-8 text-sm bg-base-200 border border-base-content/10 rounded-lg focus:outline-none focus:border-primary cursor-pointer">
              <option>Draft</option>
              <option>Review</option>
              <option>Complete</option>
            </select>
            <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 text-xs pointer-events-none" />
          </div>
          <Code>bg-base-200 border border-base-content/10 rounded-lg</Code>
        </div>
      </div>

      {/* Tags Input */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Tags / Pills Input</h3>
        <div className="bg-base-100 rounded-xl p-6 border border-base-content/10">
          <label className="block text-xs font-medium uppercase tracking-wider text-base-content/40 mb-2">
            Attributes
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs cursor-pointer hover:bg-primary/20 transition-colors flex items-center gap-2">
              Technology
              <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
            </span>
            <span className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs cursor-pointer hover:bg-primary/20 transition-colors flex items-center gap-2">
              Innovation
              <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
            </span>
          </div>
          <Code>px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs</Code>
        </div>
      </div>
    </Section>
  </div>
);

// ============================================
// CARDS SECTION
// ============================================
const CardsSection = () => (
  <div className="space-y-12">
    <Section title="Cards & Containers">
      {/* Basic Card */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Basic Card</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-base-100 rounded-lg border border-base-content/10 p-4">
            <p className="text-sm text-base-content/70">Basic card with subtle border, no shadow.</p>
          </div>
          <div className="bg-base-100 rounded-lg border border-base-content/10 p-4 hover:border-primary/30 transition-colors cursor-pointer">
            <p className="text-sm text-base-content/70">Card with hover effect (border only, no scale).</p>
          </div>
          <div className="bg-base-100 rounded-xl shadow-md p-4">
            <p className="text-sm text-base-content/70">Card with shadow (for dropdowns/modals).</p>
          </div>
        </div>
        <Code>bg-base-100 rounded-lg border border-base-content/10 p-4</Code>
      </div>

      {/* Section Card */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Section Card with Header</h3>
        <div className="bg-base-100 rounded-lg border border-base-content/10 p-4">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-base-content/10">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
              Section Title <span className="text-base-content/40 font-normal">(24)</span>
            </h3>
            <button className="text-xs text-base-content/40 hover:text-primary">View All</button>
          </div>
          <p className="text-sm text-base-content/60">Section content goes here...</p>
        </div>
      </div>

      {/* Name Tile */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Name Tiles (NameTile Component)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["small", "normal", "large"].map((size) => (
            <div key={size} className={`bg-base-100 rounded-lg border border-base-content/10 flex flex-col relative ${
              size === "small" ? "p-3 min-h-[80px]" : size === "normal" ? "p-4 min-h-[110px]" : "p-6 min-h-[160px]"
            }`}>
              {/* Grade badge */}
              <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-success/20 text-success flex items-center justify-center text-xs font-bold">A</div>
              {/* Heart */}
              <button className="absolute top-2 left-2 text-base-content/20 hover:text-primary text-sm">
                <FontAwesomeIcon icon={faHeart} />
              </button>
              {/* Name */}
              <div className="flex-1 flex items-center justify-center">
                <span className={`font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555] ${
                  size === "small" ? "text-base" : size === "normal" ? "text-xl" : "text-2xl"
                }`}>
                  Brandify
                </span>
              </div>
              <span className="text-[10px] text-base-content/30 text-center">{size}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Modal Container</h3>
        <div className="bg-base-100 rounded-xl shadow-lg border border-base-content/5 max-w-md">
          <div className="flex items-center justify-between p-5 pb-0">
            <h3 className="text-base font-semibold text-base-content">Modal Title</h3>
            <button className="text-base-content/30 hover:text-base-content/70">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="p-5">
            <p className="text-sm text-base-content/60 mb-4">Modal content goes here...</p>
            <div className="flex justify-end gap-3">
              <button className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70">Cancel</button>
              <button className="px-5 py-2 bg-primary text-primary-content rounded-full text-sm font-medium">Confirm</button>
            </div>
          </div>
        </div>
        <Code>rounded-xl shadow-lg border border-base-content/5</Code>
      </div>
    </Section>
  </div>
);

// ============================================
// BADGES SECTION
// ============================================
const BadgesSection = () => (
  <div className="space-y-12">
    <Section title="Badges & Pills">
      {/* Grade Badges */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Grade Badges</h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="text-center">
            <div className="w-8 h-8 rounded-full bg-success/20 text-success flex items-center justify-center text-sm font-bold mb-1">A</div>
            <span className="text-[10px] text-base-content/40">Small</span>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 rounded-full bg-info/20 text-info flex items-center justify-center text-base font-bold mb-1">B</div>
            <span className="text-[10px] text-base-content/40">Medium</span>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-warning/20 text-warning flex items-center justify-center text-lg font-bold mb-1">C</div>
            <span className="text-[10px] text-base-content/40">Large</span>
          </div>
        </div>
      </div>

      {/* Status Badges */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Status Badges</h3>
        <div className="flex flex-wrap gap-3">
          <span className="px-3 py-1 bg-success/20 text-success rounded-full text-xs font-medium">Complete</span>
          <span className="px-3 py-1 bg-info/20 text-info rounded-full text-xs font-medium">In Review</span>
          <span className="px-3 py-1 bg-warning/20 text-warning rounded-full text-xs font-medium">Draft</span>
          <span className="px-3 py-1 bg-error/20 text-error rounded-full text-xs font-medium">Stopped</span>
        </div>
        <Code>px-3 py-1 bg-[color]/20 text-[color] rounded-full text-xs font-medium</Code>
      </div>

      {/* Chips */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Chips (Interactive)</h3>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs border-base-content/15 text-base-content/50 hover:border-primary hover:text-primary cursor-pointer transition-colors">
              Default
            </span>
            <span className="inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs border-primary/30 text-primary hover:border-primary hover:bg-primary/5 cursor-pointer transition-colors">
              Primary
            </span>
            <span className="inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs border-primary text-primary bg-primary/5 cursor-pointer transition-colors">
              Active
            </span>
          </div>
        </div>
      </div>

      {/* Count Badges */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Count Badges</h3>
        <div className="flex flex-wrap gap-4 items-center">
          <span className="text-sm text-base-content/70">
            Names <span className="ml-1 px-2 py-0.5 bg-base-content/10 rounded-full text-xs text-base-content/50">24</span>
          </span>
          <span className="text-sm text-base-content/70">
            Liked <span className="ml-1 px-2 py-0.5 bg-primary/10 rounded-full text-xs text-primary">12</span>
          </span>
        </div>
      </div>
    </Section>
  </div>
);

// ============================================
// LOADING SECTION
// ============================================
const LoadingSection = () => (
  <div className="space-y-12">
    <Section title="Loading States">
      {/* Spinners */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Spinners (DaisyUI)</h3>
        <div className="flex flex-wrap gap-8 items-end">
          <div className="text-center">
            <span className="loading loading-ring loading-sm"></span>
            <p className="text-[10px] text-base-content/40 mt-2">loading-sm</p>
          </div>
          <div className="text-center">
            <span className="loading loading-ring loading-md"></span>
            <p className="text-[10px] text-base-content/40 mt-2">loading-md</p>
          </div>
          <div className="text-center">
            <span className="loading loading-ring loading-lg"></span>
            <p className="text-[10px] text-base-content/40 mt-2">loading-lg</p>
          </div>
        </div>
        <Code>loading loading-ring loading-[sm|md|lg]</Code>
      </div>

      {/* Loading with Text */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Loading Component</h3>
        <div className="bg-base-100 rounded-xl border border-base-content/10 p-8">
          <div className="flex flex-col justify-center items-center">
            <span className="loading loading-ring loading-md"></span>
            <span className="text-sm text-base-content/50 mt-3">Loading names...</span>
          </div>
        </div>
      </div>

      {/* Button Loading States */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Button Loading States</h3>
        <div className="flex flex-wrap gap-4">
          <button className="px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider flex items-center gap-2 opacity-70 cursor-not-allowed">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Generating...
          </button>
          <button className="px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider flex items-center gap-2 opacity-70 cursor-not-allowed">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Saving...
          </button>
        </div>
        <Code>FontAwesomeIcon with animate-spin class</Code>
      </div>

      {/* Refresh Icon */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Refresh Animation</h3>
        <div className="flex gap-4 items-center">
          <button className="flex items-center gap-2 text-sm text-base-content/40 hover:text-primary transition-colors">
            <FontAwesomeIcon icon={faRefresh} />
            Idle
          </button>
          <button className="flex items-center gap-2 text-sm text-primary">
            <FontAwesomeIcon icon={faRefresh} className="animate-spin" />
            Refreshing
          </button>
        </div>
      </div>
    </Section>
  </div>
);

// ============================================
// SPACING SECTION
// ============================================
const SpacingSection = () => (
  <div className="space-y-12">
    <Section title="Spacing & Layout">
      {/* Padding */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Common Padding</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "p-3", desc: "Small cards" },
            { label: "p-4", desc: "Default cards" },
            { label: "p-5", desc: "Modals" },
            { label: "p-6", desc: "Large sections" },
          ].map(item => (
            <div key={item.label} className="bg-base-100 rounded-lg border border-base-content/10">
              <div className={`${item.label} bg-primary/10 rounded-lg`}>
                <div className="bg-base-100 rounded text-center py-2 text-xs text-base-content/50">{item.label}</div>
              </div>
              <p className="text-[10px] text-base-content/40 text-center py-2">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Gap */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Common Gaps</h3>
        <div className="space-y-4">
          {[
            { gap: "gap-2", desc: "Icon + text, tight groups" },
            { gap: "gap-3", desc: "Button groups, card grids" },
            { gap: "gap-4", desc: "Section spacing" },
            { gap: "gap-6", desc: "Large spacing" },
          ].map(item => (
            <div key={item.gap} className="bg-base-100 rounded-lg border border-base-content/10 p-4">
              <div className={`flex ${item.gap}`}>
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="w-12 h-8 bg-primary/20 rounded"></div>
                ))}
              </div>
              <p className="text-xs text-base-content/50 mt-2">{item.gap} - {item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Grid Patterns */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Responsive Grid Patterns</h3>
        <div className="bg-base-100 rounded-lg border border-base-content/10 p-4">
          <p className="text-xs text-base-content/40 mb-3">Names Grid (resize to see responsiveness)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
              <div key={i} className="bg-primary/10 rounded-lg h-16 flex items-center justify-center text-xs text-primary/50">{i}</div>
            ))}
          </div>
          <Code>grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3</Code>
        </div>
      </div>

      {/* Shadows */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Shadows</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-base-100 rounded-lg p-4 text-center">
            <p className="text-xs text-base-content/50">No shadow (cards)</p>
            <p className="text-[10px] text-base-content/30 mt-1">border only</p>
          </div>
          <div className="bg-base-100 rounded-lg p-4 shadow-sm text-center">
            <p className="text-xs text-base-content/50">shadow-sm</p>
          </div>
          <div className="bg-base-100 rounded-lg p-4 shadow-md text-center">
            <p className="text-xs text-base-content/50">shadow-md (dropdowns)</p>
          </div>
          <div className="bg-base-100 rounded-lg p-4 shadow-lg text-center">
            <p className="text-xs text-base-content/50">shadow-lg (modals)</p>
          </div>
        </div>
      </div>

      {/* Border Radius */}
      <div className="mb-10">
        <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-4">Border Radius</h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/20 rounded"></div>
            <p className="text-[10px] text-base-content/40 mt-1">rounded</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/20 rounded-lg"></div>
            <p className="text-[10px] text-base-content/40 mt-1">rounded-lg (cards)</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/20 rounded-xl"></div>
            <p className="text-[10px] text-base-content/40 mt-1">rounded-xl (modals)</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/20 rounded-full"></div>
            <p className="text-[10px] text-base-content/40 mt-1">rounded-full</p>
          </div>
        </div>
      </div>
    </Section>
  </div>
);

// ============================================
// HELPER COMPONENTS
// ============================================
const Section = ({ title, variant, children }) => (
  <section className={`bg-base-100 rounded-xl p-6 border ${
    variant === "error" ? "border-error/20" : variant === "success" ? "border-success/20" : "border-base-content/10"
  }`}>
    <div className="flex items-center gap-2 mb-6">
      {variant && <div className={`w-3 h-3 rounded-full ${variant === "error" ? "bg-error" : "bg-success"}`}></div>}
      <h2 className="text-lg font-semibold text-base-content">{title}</h2>
    </div>
    {children}
  </section>
);

const ExampleCard = ({ title, status, children }) => (
  <div className={`bg-base-200/50 rounded-lg p-4 ${status === "warning" ? "border border-error/30" : ""}`}>
    <p className="text-xs text-base-content/40 mb-3">
      {title} {status === "warning" && <span className="text-error">⚠ Different</span>}
    </p>
    {children}
  </div>
);

const Code = ({ children, variant }) => (
  <code className={`text-[10px] px-2 py-1 rounded block mt-2 font-mono ${
    variant === "error" ? "bg-error/10 text-error/70" : "bg-base-content/5 text-base-content/50"
  }`}>
    {children}
  </code>
);

const ColorSwatch = ({ name, color, variable, dark }) => (
  <div className="text-center">
    <div
      className={`w-full h-16 rounded-lg border ${dark ? "border-base-content/10" : "border-transparent"}`}
      style={{ backgroundColor: color }}
    ></div>
    <p className="text-xs text-base-content/70 mt-2">{name}</p>
    <p className="text-[10px] text-base-content/40">{color}</p>
    <p className="text-[10px] text-base-content/30">{variable}</p>
  </div>
);

const GradeBadge = ({ grade }) => {
  const colors = {
    A: "bg-success/20 text-success",
    B: "bg-info/20 text-info",
    C: "bg-warning/20 text-warning",
    D: "bg-error/20 text-error",
    F: "bg-error/30 text-error",
  };
  return (
    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${colors[grade]}`}>
      {grade}
    </div>
  );
};

export default StyleGuide;
